import { type ApiRequisition, EnumContainerActionRequest } from '@Model';
import { type AsyncStateUpdate, type MetaData, type WizardStep } from '@synqo/frontend-component-library';
import { actionVerb, type RequisitionAction, toActionRequest } from '@utils/requisitionAction';
import { type ContainerPosition, type ContainerStockLine, plannedActionOf } from './containerOrder';
import { type RequisitionWizardState } from './RequisitionWizardState';
import { type ContainerSlot } from './useContainerSlot';
import {
    type ContainerStockView,
    defineActionStep,
    defineDetailsStep,
    defineExchangeStep,
    definePositionsStep,
    defineSiteStep,
    defineSummaryStep,
} from './steps';

export interface BuildWizardStepsOptions {
    data: ApiRequisition;
    meta: MetaData<ApiRequisition> | undefined;
    apiResourcePath: string;
    canEdit: boolean;
    /** Whether the container service is switched on. Off, this is the three-step material wizard. */
    withContainers: boolean;
    action: RequisitionAction | undefined;
    /** The first container action of the order and, on an exchange, the second. */
    slots: readonly [ContainerSlot, ContainerSlot];
    /** Every container of the order, which the summary lists. */
    positions: readonly ContainerPosition[];
    positionCount: number;
    /** What switching the action would throw away, whichever of the two kinds the order is. */
    discardableCount: number;
    stock: ContainerStockView;
    /** How many containers stand at the site, which the tiles picking from the stock show as a pill. */
    stockTotal: number;
    /** The waste code of the content of each slot, empty while none is known. */
    avvCodes: readonly [string, string];
    onChooseAction: (action: RequisitionAction) => void;
    onTakeLine: (line: ContainerStockLine) => void;
    onRemoveFirst: () => void;
    onRemovePosition: () => void;
    requestUpdateState: AsyncStateUpdate<ApiRequisition>;
    i18n: (key: string) => string;
}

/** Index of the step the summary's "Ändern" link leads back to, with and without containers. */
export const detailsStepIndexFor = (withContainers: boolean): number => (withContainers ? 2 : 1);

/**
 * The steps the wizard runs through.
 *
 * With the container service switched off this is the three-step material wizard it has always
 * been. With it on, the action the customer takes sits between site and details and decides what
 * the third step asks — which is why the action and its container are held above the steps.
 */
// @ai-generated 2026-09-11T11:42:54+00:00 by matthias.gessinger@synqony.com using Claude Opus 5
export function buildWizardSteps({
    data,
    meta,
    apiResourcePath,
    canEdit,
    withContainers,
    action,
    slots,
    positions,
    positionCount,
    discardableCount,
    stock,
    stockTotal,
    avvCodes,
    onChooseAction,
    onTakeLine,
    onRemoveFirst,
    onRemovePosition,
    requestUpdateState,
    i18n,
}: BuildWizardStepsOptions): WizardStep<RequisitionWizardState>[] {
    const siteStep = defineSiteStep({
        data,
        meta,
        apiResourcePath,
        canEdit,
        siteChangeGuard: { positionCount, discard: onRemovePosition },
        requestUpdateState,
        i18n,
    });

    const summaryStep = defineSummaryStep({
        data,
        meta,
        canEdit,
        action: withContainers ? action : undefined,
        containerPositions: withContainers ? positions : [],
        detailsStepIndex: detailsStepIndexFor(withContainers),
        requestUpdateState,
        i18n,
    });

    if (!withContainers) {
        return [
            siteStep,
            definePositionsStep({ data, apiResourcePath, canEdit, requestUpdateState, i18n }),
            summaryStep,
        ];
    }

    const [first, second] = slots;
    const request = toActionRequest(action);
    const stepOf = (): WizardStep<RequisitionWizardState> => {
        if (request === undefined) {
            return definePositionsStep({
                data,
                apiResourcePath,
                canEdit,
                label: i18n(actionVerb(action)),
                // Der Zähler steht im Footer des Container-Flows; zweimal ist einmal zu viel.
                withCounter: false,
                requestUpdateState,
                i18n,
            });
        }

        if (request === EnumContainerActionRequest.Exchange) {
            return defineExchangeStep({
                pickup: {
                    position: first.position,
                    containerTypeField: first.containerTypeField,
                    contentField: first.contentField,
                    avvCode: avvCodes[0],
                    onCount: first.changeCount,
                    onLocationHint: first.setLocationHint,
                    onContainerNumber: first.setContainerNumber,
                    onRemove: onRemoveFirst,
                },
                delivery: {
                    position: second.position,
                    containerTypeField: second.containerTypeField,
                    contentField: second.contentField,
                    avvCode: avvCodes[1],
                    onCount: second.changeCount,
                    onLocationHint: second.setLocationHint,
                },
                stock,
                onTakeLine,
                i18n,
            });
        }

        return defineDetailsStep({
            action: request,
            stock,
            position: first.position,
            containerTypeField: first.containerTypeField,
            contentField: first.contentField,
            avvCode: avvCodes[0],
            onTakeLine,
            onCount: first.changeCount,
            onContainerNumber: first.setContainerNumber,
            onLocationHint: first.setLocationHint,
            onRemove: onRemoveFirst,
            i18n,
        });
    };

    return [
        siteStep,
        defineActionStep({
            data,
            stockTotal,
            plannedTotal: stock.lines
                .filter((line) => plannedActionOf(line))
                .reduce((sum, line) => sum + line.stockCount, 0),
            isStockLoading: stock.isLoading,
            action,
            discardableCount,
            onChoose: onChooseAction,
            i18n,
        }),
        stepOf(),
        summaryStep,
    ];
}
